import React, { useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Switch,
  DatePicker,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Statistic,
  Upload,
  Image,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  UploadOutlined,
  EyeOutlined,
  CalendarOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { getStorageUrl } from '../utils/storageUtils';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Banners = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const previewTitle = '';
  const queryClient = useQueryClient();

  // Fetch banners with filters
  const { data: banners = [], isLoading } = useQuery(
    ['banners', searchTerm, statusFilter],
    async () => {
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (statusFilter) params.append('status', statusFilter);

        const response = await api.get(`/admin/banners?${params.toString()}`);
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch banners:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 30000,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Client-side filtering fallback
  const filteredBanners = useMemo(() => {
    if (!Array.isArray(banners)) return [];
    let filtered = [...banners];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          (b.title && b.title.toLowerCase().includes(searchLower)) ||
          (b.description && b.description.toLowerCase().includes(searchLower)),
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((b) => b.status === statusFilter);
    }

    return filtered;
  }, [banners, searchTerm, statusFilter]);

  // Create banner mutation
  const createBannerMutation = useMutation(
    async (bannerData) => {
      const formData = new FormData();

      Object.keys(bannerData).forEach((key) => {
        if (key !== 'image' && bannerData[key] !== undefined) {
          formData.append(key, bannerData[key]);
        }
      });

      const fileObj = Array.isArray(bannerData.image) ? bannerData.image[0] : bannerData.image;
      if (fileObj?.originFileObj) {
        formData.append('image', fileObj.originFileObj);
      }

      const response = await api.post('/admin/banners', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Banner created successfully');
        setIsModalVisible(false);
        form.resetFields();
        setPreviewImage(null);
        queryClient.invalidateQueries('banners');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to create banner');
      },
    },
  );

  // Update banner mutation
  const updateBannerMutation = useMutation(
    async ({ id, ...bannerData }) => {
      const formData = new FormData();

      Object.keys(bannerData).forEach((key) => {
        if (key !== 'image' && bannerData[key] !== undefined) {
          formData.append(key, bannerData[key]);
        }
      });

      const fileObj = Array.isArray(bannerData.image) ? bannerData.image[0] : bannerData.image;
      if (fileObj?.originFileObj) {
        formData.append('image', fileObj.originFileObj);
      }

      formData.append('_method', 'PUT');

      const response = await api.post(`/admin/banners/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Banner updated successfully');
        setIsModalVisible(false);
        setEditingBanner(null);
        form.resetFields();
        setPreviewImage(null);
        queryClient.invalidateQueries('banners');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update banner');
      },
    },
  );

  // Delete banner mutation
  const deleteBannerMutation = useMutation(
    async (id) => {
      await api.delete(`/admin/banners/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Banner deleted successfully');
        queryClient.invalidateQueries('banners');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete banner');
      },
    },
  );

  // Toggle banner status mutation
  const toggleStatusMutation = useMutation(
    async ({ id, status }) => {
      await api.patch(`/admin/banners/${id}/toggle-status`, { status });
    },
    {
      onSuccess: () => {
        message.success('Banner status updated successfully');
        queryClient.invalidateQueries('banners');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update banner status');
      },
    },
  );

  const handleSubmit = async (values) => {
    try {
      // Format dates
      if (values.startDate) {
        values.start_date = values.startDate.format('YYYY-MM-DD');
        delete values.startDate;
      }
      if (values.endDate) {
        values.end_date = values.endDate.format('YYYY-MM-DD');
        delete values.endDate;
      }

      if (editingBanner) {
        updateBannerMutation.mutate({ id: editingBanner.id, ...values });
      } else {
        createBannerMutation.mutate(values);
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    form.setFieldsValue({
      ...banner,
      startDate: banner.start_date ? dayjs(banner.start_date) : null,
      endDate: banner.end_date ? dayjs(banner.end_date) : null,
    });
    setPreviewImage(banner.image_url);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    deleteBannerMutation.mutate(id);
  };

  const handleToggleStatus = (banner) => {
    const newStatus = banner.status === 'active' ? 'inactive' : 'active';
    toggleStatusMutation.mutate({ id: banner.id, status: newStatus });
  };

  const uploadProps = {
    name: 'image',
    listType: 'picture-card',
    className: 'avatar-uploader',
    showUploadList: false,
    beforeUpload: (file) => {
      const isJpgOrPng =
        file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG/PNG/WEBP file!');
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must smaller than 2MB!');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange: (info) => {
      if (info.file.status === 'removed') {
        // Revoke old blob URL before clearing
        if (previewImage && previewImage.startsWith('blob:')) {
          URL.revokeObjectURL(previewImage);
        }
        setPreviewImage(null);
        form.setFieldsValue({ image: null });
      } else if (info.file.originFileObj) {
        // Revoke old blob URL before creating new one
        if (previewImage && previewImage.startsWith('blob:')) {
          URL.revokeObjectURL(previewImage);
        }
        setPreviewImage(URL.createObjectURL(info.file.originFileObj));
      }
    },
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image_url',
      key: 'image_url',
      render: (imageUrl, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {imageUrl ? (
            <Image
              src={getStorageUrl(imageUrl)}
              alt={record.title}
              style={{
                width: 60,
                height: 40,
                objectFit: 'cover',
                borderRadius: '4px',
                border: '1px solid #f0f0f0',
              }}
              preview={{
                mask: <EyeOutlined />,
                maskClosable: true,
              }}
            />
          ) : (
            <div
              style={{
                width: 60,
                height: 40,
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #d9d9d9',
              }}
            >
              <PictureOutlined style={{ color: '#bfbfbf', fontSize: '18px' }} />
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      render: (title) => (
        <Tooltip title={title}>
          <span>{title}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
      render: (description) => (
        <Tooltip title={description}>
          <span>{description}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) => (a.type || '').localeCompare(b.type || ''),
      render: (type) => (
        <Tag color={type === 'promotion' ? 'orange' : type === 'announcement' ? 'blue' : 'green'}>
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      render: (position) => <Tag color="purple">{position || 'HOME'}</Tag>,
    },
    {
      title: 'Display Period',
      key: 'display_period',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            From:{' '}
            {record.start_date ? dayjs(record.start_date).format('MMM DD, YYYY') : 'No start date'}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            To: {record.end_date ? dayjs(record.end_date).format('MMM DD, YYYY') : 'No expiry'}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>{status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.status === 'active' ? 'Deactivate' : 'Activate'}>
            <Switch
              size="small"
              checked={record.status === 'active'}
              onChange={() => handleToggleStatus(record)}
              loading={toggleStatusMutation.isLoading}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this banner?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deleteBannerMutation.isLoading}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Calculate statistics
  const activeBanners = banners.filter((b) => b.status === 'active').length;
  const promotionBanners = banners.filter((b) => b.type === 'promotion').length;

  return (
    <div>
      <Title level={2}>
        <PictureOutlined /> Banners Management
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Banners"
              value={banners.length}
              prefix={<PictureOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Banners"
              value={activeBanners}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Promotions"
              value={promotionBanners}
              prefix={<LinkOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Announcements"
              value={banners.filter((b) => b.type === 'announcement').length}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* Filters and Actions */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search banners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="expired">Expired</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={10} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingBanner(null);
                form.resetFields();
                setPreviewImage(null);
                setIsModalVisible(true);
              }}
            >
              Add New Banner
            </Button>
          </Col>
        </Row>

        {/* Banners Table */}
        <Table
          columns={columns}
          dataSource={filteredBanners}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: filteredBanners.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} banners`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Add/Edit Banner Modal */}
      <Modal
        title={editingBanner ? 'Edit Banner' : 'Add New Banner'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingBanner(null);
          form.resetFields();
          setPreviewImage(null);
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="title"
                label="Banner Title"
                rules={[{ required: true, message: 'Please enter banner title' }]}
              >
                <Input placeholder="Enter banner title" maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="type"
                label="Banner Type"
                rules={[{ required: true, message: 'Please select banner type' }]}
              >
                <Select placeholder="Select banner type">
                  <Option value="promotion">Promotion</Option>
                  <Option value="announcement">Announcement</Option>
                  <Option value="advertisement">Advertisement</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="position" label="Display Position" initialValue="home">
                <Select placeholder="Select display position">
                  <Option value="home">Home Page</Option>
                  <Option value="category">Category Page</Option>
                  <Option value="product">Product Page</Option>
                  <Option value="sidebar">Sidebar</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status" label="Status" initialValue="active">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="image" label="Banner Image" valuePropName="file">
            <Upload {...uploadProps} customRequest={({ onSuccess }) => onSuccess('ok')}>
              {previewImage ? (
                <img
                  src={getStorageUrl(previewImage)}
                  alt="banner"
                  style={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: 200,
                    backgroundColor: '#fafafa',
                    border: '1px dashed #d9d9d9',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <UploadOutlined
                    style={{ fontSize: '34px', color: '#d9d9d9', marginBottom: '8px' }}
                  />
                  <div style={{ color: '#666' }}>Click or drag image to upload</div>
                  <div style={{ color: '#999', fontSize: '14px', marginTop: '4px' }}>
                    JPG, PNG, WEBP (Max 2MB)
                  </div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter banner description' }]}
          >
            <TextArea rows={3} placeholder="Enter banner description" maxLength={500} showCount />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="startDate" label="Start Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="endDate" label="End Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="link_url" label="Link URL (Optional)">
            <Input placeholder="https://example.com" addonBefore={<LinkOutlined />} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingBanner(null);
                  form.resetFields();
                  setPreviewImage(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createBannerMutation.isLoading || updateBannerMutation.isLoading}
              >
                {editingBanner ? 'Update Banner' : 'Create Banner'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt={previewTitle} style={{ width: '100%' }} src={getStorageUrl(previewImage)} />
      </Modal>
    </div>
  );
};

export default Banners;
